'use client';

import { motion } from 'motion/react';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import type { PhilosophySectionContent } from '@/sanity/lib/sections';
import { SectionLayout } from '../shared/section-layout';

export default function PhilosophySection({
  content,
}: {
  content?: PhilosophySectionContent | null;
}) {
  const {
    sectionHeaderItemVariants,
    sectionHeaderRevealVariants,
    sectionSequenceBlockVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();

  if (!content || content.show === false) return null;

  const { primary, secondary } = sectionCtaPairFromContent(content.showCTA, content.ctaButtons);

  return (
    <SectionLayout
      className="relative bg-neutral-900 dark:bg-black py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
        className="text-left sm:text-center"
      >
        <motion.div variants={sectionHeaderRevealVariants}>
          {content.showHeader !== false ? (
            <>
              {content.header?.tagline ? (
                <motion.p
                  variants={sectionHeaderItemVariants}
                  className="text-[10px] sm:text-xs font-semibold uppercase tracking-widest text-[#FCFAD7]/80"
                >
                  {content.header.tagline}
                </motion.p>
              ) : null}
              <motion.p
                variants={sectionHeaderItemVariants}
                className="mx-auto mt-4 text-xl sm:text-2xl leading-tight tracking-wide text-white xl:text-3xl font-thin lg:max-w-3xl text-pretty"
              >
                &ldquo;
                <SanityInlineText
                  blocks={content.header?.heading}
                  emphasisClassName="italic font-normal text-[#FCFAD7] dark:text-[#FCFAD7]"
                />
                &rdquo;
              </motion.p>
              {content.header?.subheading ? (
                <motion.p
                  variants={sectionHeaderItemVariants}
                  className="mt-4 text-[10px] sm:text-xs font-light uppercase tracking-wide text-muted-foreground sm:mt-6"
                >
                  — {content.header.subheading}
                </motion.p>
              ) : null}
            </>
          ) : null}
        </motion.div>
        {content.showHeader !== false && primary ? (
          <motion.div
            variants={sectionSequenceBlockVariants}
            className="mt-8 flex justify-center sm:mt-10"
          >
            <SectionCtaPair primary={primary} secondary={secondary} trackingLocation="philosophy" />
          </motion.div>
        ) : null}
        {content.showHeader === false && primary ? (
          <motion.div variants={sectionSequenceBlockVariants} className="flex justify-center">
            <SectionCtaPair primary={primary} secondary={secondary} trackingLocation="philosophy" />
          </motion.div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
